import React, { useContext, createContext, useMemo } from 'react'
import { getCollectionId, useSlsStateContext } from '@fs/zion-sls'
import { useFeatureFlag } from '@fs/zion-flags'
import { useR2StateContext } from '../r2Provider/R2Context'
import { SPECIAL_COLLECTIONS, ALL } from './collectionConstants'

export const CollectionContext = createContext({})

/**
 * CollectionProvider component that provides collection-related context to its children.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The child components that will receive the context.
 * @returns {JSX.Element} The CollectionContext provider with the specified value.
 */
export function CollectionProvider({ children }) {
  const zionSlsState = useSlsStateContext()
  const r2State = useR2StateContext()
  const isOttomanEnabled = useFeatureFlag('rip_sharedImageViewer_OttomanCensusEdit')
  const currentImage = useMemo(
    () => zionSlsState?.images?.find((img) => img.partitionKey === zionSlsState.currentImageArk),
    [zionSlsState]
  )
  const collectionId = useMemo(() => currentImage && getCollectionId(currentImage), [currentImage])
  // if an image is deprecated restrict all fields
  const restrictAllFields = currentImage?.deprecatedImage ? ALL : null
  let restrictedFields = isOttomanEnabled.isOn
    ? []
    : restrictAllFields || SPECIAL_COLLECTIONS[collectionId]?.restrictedFields || [] // white list of fields that are editable

  // Disable editing on genealogy-type records which need a different record format
  if (
    r2State?.groupData?.imageViewerCoverages?.some((coverage) => {
      let hasTreeType = false
      coverage.recordTypes.forEach((coverageRecordType) => {
        const recordType = zionSlsState.types?.records?.find((type) => type.conceptId === coverageRecordType.id)
        hasTreeType = recordType?.tree
      })
      return hasTreeType
    })
  ) {
    restrictedFields = ALL
  }
  // eslint-disable-next-line react/jsx-no-constructed-context-values
  return <CollectionContext.Provider value={{ collectionId, restrictedFields }}>{children}</CollectionContext.Provider>
}

/**
 * Custom hook to use the CollectionContext.
 *
 * @returns {Object} The context value containing collectionId, restrictedFields, and limited.
 */
export const useCollectionContextProvider = () => {
  return useContext(CollectionContext)
}

export default CollectionProvider
